import React, { useEffect, useMemo, useState } from 'react';
import MetricCard from '../components/MetricCard';
import { usePortal } from '../context/PortalContext';
import { clubService } from '../services/clubService';
import {
  ClubSubscriptionBillingType,
  ClubSubscriptionContract,
  ClubSubscriptionInvoice,
  ClubSubscriptionPlan,
  ClubSubscriptionStatus,
} from '../types';
import { formatDateTime } from '../utils/formatters';

const getSubscriptionStatusLabel = (status: ClubSubscriptionStatus['status']): string => {
  switch (status) {
    case 'ACTIVE':
      return 'Ativa';
    case 'TRIAL':
      return 'Trial';
    case 'EXPIRED':
      return 'Expirada';
    case 'CANCELLED':
      return 'Cancelada';
    case 'SUSPENDED':
      return 'Suspensa';
    default:
      return 'Sem assinatura';
  }
};

const getSubscriptionToneClass = (status: ClubSubscriptionStatus['status']): string => {
  switch (status) {
    case 'ACTIVE':
      return 'portal-status-pill--positive';
    case 'TRIAL':
      return 'portal-status-pill--warning';
    case 'EXPIRED':
    case 'CANCELLED':
    case 'SUSPENDED':
      return 'portal-status-pill--danger';
    default:
      return 'portal-status-pill--neutral';
  }
};

const getHistoryChangeLabel = (value: ClubSubscriptionStatus['history'][number]['change_type']): string => {
  switch (value) {
    case 'INSERT':
      return 'Criacao';
    case 'UPDATE':
      return 'Renovacao/Ajuste';
    case 'BACKFILL':
      return 'Carga inicial';
    default:
      return value;
  }
};

const formatDaysRemaining = (value: number | null): string => {
  if (value === null) {
    return 'Sem expiracao';
  }

  if (value < 0) {
    return `${Math.abs(value)} dia(s) atrasado(s)`;
  }

  if (value === 0) {
    return 'Vence hoje';
  }

  return `${value} dia(s) restantes`;
};

const getSubscriptionTheme = (status: ClubSubscriptionStatus['status'] | null): 'receivable' | 'warning' | 'fee' => {
  switch (status) {
    case 'ACTIVE':
      return 'receivable';
    case 'TRIAL':
      return 'warning';
    default:
      return 'fee';
  }
};

const BILLING_TYPE_OPTIONS: Array<{ value: ClubSubscriptionBillingType; label: string }> = [
  { value: 'PIX', label: 'Pix' },
  { value: 'BOLETO', label: 'Boleto' },
  { value: 'CREDIT_CARD', label: 'Cartao de credito' },
];
const CLUB_OWNER_TERMS_VERSION = '1.0.0';

const getInvoiceStatusLabel = (status: string | null | undefined): string => {
  const normalized = String(status || '').toUpperCase();
  switch (normalized) {
    case 'PENDING':
      return 'Pendente';
    case 'RECEIVED':
      return 'Pago';
    case 'CONFIRMED':
      return 'Confirmado';
    case 'OVERDUE':
      return 'Vencido';
    case 'REFUNDED':
      return 'Estornado';
    case 'DELETED':
      return 'Removido';
    default:
      return normalized || 'Sem cobranca';
  }
};

const getPlanFeatureList = (plan: ClubSubscriptionPlan): string[] => {
  const descriptionParts = String(plan.description || '')
    .split(/[\n;|]+/g)
    .map((item) => item.trim())
    .filter(Boolean)
    .slice(0, 4);

  if (descriptionParts.length) {
    return descriptionParts;
  }

  const defaults = [
    plan.billing_cycle === 'MONTHLY' ? 'Cobranca mensal automatica e previsivel' : 'Ciclo de cobranca alinhado ao seu plano',
    'Acesso completo ao painel de operacao e governanca do club',
    'Controle comercial com status operacional em tempo real',
    'Gestao de assinatura e cobrancas no proprio portal',
  ];

  if (typeof plan.trial_days === 'number' && plan.trial_days > 0) {
    defaults.unshift(`Trial de ${plan.trial_days} dia(s)`);
  }

  return defaults.slice(0, 4);
};

const SubscriptionPage: React.FC = () => {
  const { selectedClub } = usePortal();
  const [subscription, setSubscription] = useState<ClubSubscriptionStatus | null>(null);
  const [plans, setPlans] = useState<ClubSubscriptionPlan[]>([]);
  const [contract, setContract] = useState<ClubSubscriptionContract | null>(null);
  const [invoice, setInvoice] = useState<ClubSubscriptionInvoice | null>(null);
  const [invoiceHistory, setInvoiceHistory] = useState<ClubSubscriptionInvoice[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [selectedPlanCode, setSelectedPlanCode] = useState('');
  const [selectedBillingType, setSelectedBillingType] = useState<ClubSubscriptionBillingType>('PIX');
  const [cancelReason, setCancelReason] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);

  const loadSubscriptionContext = async (clubId: string) => {
    const nextSubscription = await clubService.getClubSubscription(clubId);
    const [nextPlans, nextContract, nextInvoice] = await Promise.all([
      clubService.listClubSubscriptionPlans(clubId),
      clubService.getCurrentClubSubscriptionContract(clubId),
      clubService.getCurrentClubSubscriptionInvoice(clubId),
    ]);
    const nextInvoiceHistory = await clubService.listClubSubscriptionInvoices(clubId, 20);

    setSubscription(nextSubscription);
    setPlans(nextPlans);
    setContract(nextContract);
    setInvoice(nextInvoice);
    setInvoiceHistory(nextInvoiceHistory);
  };

  useEffect(() => {
    if (!selectedClub?.id) {
      setSubscription(null);
      setPlans([]);
      setContract(null);
      setInvoice(null);
      setInvoiceHistory([]);
      setError('');
      setSuccessMessage('');
      return;
    }

    setLoading(true);
    setError('');
    setSuccessMessage('');

    loadSubscriptionContext(selectedClub.id)
      .catch((nextError: any) => setError(nextError.message || 'Erro ao carregar a assinatura do club'))
      .finally(() => setLoading(false));
  }, [selectedClub?.id]);

  useEffect(() => {
    if (!plans.length) {
      setSelectedPlanCode('');
      return;
    }

    if (selectedPlanCode && plans.some((plan) => plan.plan_code === selectedPlanCode)) {
      return;
    }

    const defaultPlan = plans.find((plan) => plan.is_default) || plans[0];
    setSelectedPlanCode(defaultPlan?.plan_code || '');
  }, [plans, selectedPlanCode]);

  useEffect(() => {
    if (contract?.payment_method_preference) {
      const normalized = String(contract.payment_method_preference).toUpperCase();
      if (normalized === 'PIX' || normalized === 'BOLETO' || normalized === 'CREDIT_CARD') {
        setSelectedBillingType(normalized);
      }
    }
  }, [contract?.payment_method_preference]);

  const runAction = async (
    action: () => Promise<unknown>,
    successLabel: string
  ) => {
    if (!selectedClub?.id) {
      return;
    }

    setSubmitting(true);
    setError('');
    setSuccessMessage('');

    try {
      await action();
      await loadSubscriptionContext(selectedClub.id);
      setSuccessMessage(successLabel);
    } catch (nextError: any) {
      setError(nextError.message || 'Falha ao processar a acao de assinatura');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreateContract = async () => {
    if (!selectedClub?.id || !selectedPlanCode) {
      setError('Selecione um plano antes de contratar.');
      return;
    }
    if (!termsAccepted) {
      setError('Para contratar assinatura, aceite o termo de assinatura SaaS.');
      return;
    }

    await runAction(
      () =>
        clubService.createClubSubscriptionContract(selectedClub.id, {
          planCode: selectedPlanCode,
          billingType: selectedBillingType,
          termsAccepted: true,
          termsVersion: CLUB_OWNER_TERMS_VERSION,
        }),
      'Assinatura criada com sucesso.'
    );
  };

  const handleUpdateContract = async () => {
    if (!selectedClub?.id || !selectedPlanCode) {
      setError('Selecione um plano para atualizar.');
      return;
    }

    await runAction(
      () =>
        clubService.updateCurrentClubSubscriptionContract(selectedClub.id, {
          planCode: selectedPlanCode,
          billingType: selectedBillingType,
          updatePendingPayments: true,
        }),
      'Contrato atualizado com sucesso.'
    );
  };

  const handleCancelContract = async () => {
    if (!selectedClub?.id) {
      return;
    }

    await runAction(
      () =>
        clubService.cancelCurrentClubSubscriptionContract(selectedClub.id, {
          reason: cancelReason.trim() || undefined,
        }),
      'Contrato cancelado com sucesso.'
    );
  };

  const handleResumeContract = async () => {
    if (!selectedClub?.id) {
      return;
    }
    if (!termsAccepted) {
      setError('Para retomar assinatura, aceite o termo de assinatura SaaS.');
      return;
    }

    await runAction(
      () =>
        clubService.resumeClubSubscriptionContract(selectedClub.id, {
          planCode: selectedPlanCode || undefined,
          billingType: selectedBillingType,
          termsAccepted: true,
          termsVersion: CLUB_OWNER_TERMS_VERSION,
        }),
      'Assinatura retomada com sucesso.'
    );
  };

  const handleRefreshInvoice = async () => {
    if (!selectedClub?.id) {
      return;
    }

    await runAction(
      () => clubService.getCurrentClubSubscriptionInvoice(selectedClub.id),
      'Cobranca sincronizada.'
    );
  };

  const statusLabel = useMemo(
    () => getSubscriptionStatusLabel(subscription?.status || null),
    [subscription?.status]
  );
  const history = subscription?.history || [];
  const canOperate = Boolean(subscription?.can_operate_club);
  const canCreate = Boolean(subscription?.can_create_club);
  const hasCurrentContract = Boolean(contract && contract.status !== 'CANCELLED' && contract.status !== 'EXPIRED');
  const isCancelledContract = contract?.status === 'CANCELLED' || contract?.status === 'EXPIRED';
  const showPlanShowcase = !subscription?.has_subscription;

  return (
    <div className="portal-page-grid portal-page-stack">
      <section className="portal-section-card portal-section-card--hero portal-section-card--dashboard-hero">
        <div className="portal-section-card__header">
          <div>
            <p className="portal-section-card__eyebrow">Assinatura do operador</p>
            <h3>Status operacional do club</h3>
          </div>
          <span className={`portal-status-pill ${getSubscriptionToneClass(subscription?.status || null)}`}>
            {statusLabel}
          </span>
        </div>
        <p className="portal-muted">
          Esta tela mostra a situacao comercial que libera ou bloqueia criar clubs, criar mesas e iniciar jogos.
        </p>

        <div className="portal-dashboard-hero-stats">
          <article className="portal-dashboard-hero-stats__item">
            <span>Status</span>
            <strong>{statusLabel}</strong>
          </article>
          <article className="portal-dashboard-hero-stats__item">
            <span>Plano</span>
            <strong>{subscription?.plan_code || 'Sem plano'}</strong>
          </article>
          <article className="portal-dashboard-hero-stats__item">
            <span>Operar club</span>
            <strong>{canOperate ? 'Liberado' : 'Bloqueado'}</strong>
          </article>
          <article className="portal-dashboard-hero-stats__item">
            <span>Expiracao</span>
            <strong>{formatDaysRemaining(subscription?.expires_in_days ?? null)}</strong>
          </article>
        </div>
      </section>

      {error ? <div className="portal-banner portal-banner--error">{error}</div> : null}
      {successMessage ? <div className="portal-banner portal-banner--success">{successMessage}</div> : null}
      {loading ? <div className="portal-loading-screen">Carregando assinatura...</div> : null}

      <section className="portal-metrics-grid portal-dashboard-metrics-grid">
        <MetricCard
          label="Status"
          value={statusLabel}
          tone={canOperate ? 'positive' : 'warning'}
          variant="dashboard"
          theme={getSubscriptionTheme(subscription?.status || null)}
          badge="Status"
        />
        <MetricCard
          label="Plano"
          value={subscription?.plan_code || 'Nao configurado'}
          variant="dashboard"
          theme="rake"
          badge="Plano"
        />
        <MetricCard
          label="Expiracao"
          value={formatDaysRemaining(subscription?.expires_in_days ?? null)}
          helper={formatDateTime(subscription?.expires_at)}
          tone={canOperate ? 'positive' : 'warning'}
          variant="dashboard"
          theme={canOperate ? 'balance' : 'fee'}
          badge="Prazo"
        />
        <MetricCard
          label="Criar club"
          value={canCreate ? 'Liberado' : 'Bloqueado'}
          tone={canCreate ? 'positive' : 'warning'}
          variant="dashboard"
          theme={canCreate ? 'receivable' : 'warning'}
          badge="Permissao"
        />
        <MetricCard
          label="Operar club"
          value={canOperate ? 'Liberado' : 'Bloqueado'}
          tone={canOperate ? 'positive' : 'warning'}
          variant="dashboard"
          theme={canOperate ? 'deposit' : 'fee'}
          badge="Permissao"
        />
        <MetricCard
          label="Dono responsavel"
          value={subscription?.owner_name || selectedClub?.name || 'Club'}
          helper={subscription?.club_name || selectedClub?.name || ''}
          variant="dashboard"
          theme="warning"
          badge="Owner"
        />
      </section>

      {showPlanShowcase ? (
        <section className="portal-section-card portal-section-card--finance-highlight portal-plan-showcase">
          <div className="portal-section-card__header">
            <div>
              <p className="portal-section-card__eyebrow">Escolha seu plano</p>
              <h3>Comece a operar com assinatura ativa</h3>
              <p className="portal-muted">
                Escolha o plano ideal para o seu momento, ative em poucos passos e mantenha seu club liberado para operar sem interrupcoes.
              </p>
            </div>
          </div>

          <div className="portal-plan-showcase__grid">
            {plans.length ? (
              plans.map((plan) => {
                const selected = selectedPlanCode === plan.plan_code;
                return (
                  <article
                    key={plan.plan_code}
                    className={`portal-plan-card ${selected ? 'portal-plan-card--selected' : ''}`}
                  >
                    <header>
                      <div>
                        <strong>{plan.display_name}</strong>
                        <span>{plan.plan_code}</span>
                      </div>
                      {plan.is_default ? <span className="portal-chip">Padrao</span> : null}
                    </header>

                    <div className="portal-plan-card__price">
                      <strong>{plan.currency_code} {Number(plan.price_reais || 0).toFixed(2)}</strong>
                      <span>{plan.billing_cycle}</span>
                    </div>

                    <ul>
                      {getPlanFeatureList(plan).map((feature) => (
                        <li key={`${plan.plan_code}-${feature}`}>{feature}</li>
                      ))}
                    </ul>

                    <button
                      type="button"
                      className={selected ? 'portal-primary-button' : 'portal-secondary-button'}
                      onClick={() => setSelectedPlanCode(plan.plan_code)}
                    >
                      {selected ? 'Plano selecionado' : 'Quero este plano'}
                    </button>
                  </article>
                );
              })
            ) : (
              <div className="portal-banner portal-banner--warning">Nenhum plano disponivel no catalogo.</div>
            )}
          </div>
        </section>
      ) : null}

      <div className="portal-page-grid portal-page-grid--two-columns">
        <section className="portal-section-card portal-section-card--finance-highlight portal-section-card--finance-balance">
          <div className="portal-section-card__header">
            <div>
              <p className="portal-section-card__eyebrow">Contrato</p>
              <h3>Dados da assinatura</h3>
            </div>
          </div>

          <dl className="portal-detail-grid">
            <div>
              <dt>Club</dt>
              <dd>{subscription?.club_name || selectedClub?.name || '-'}</dd>
            </div>
            <div>
              <dt>Dono</dt>
              <dd>{subscription?.owner_name || '-'}</dd>
            </div>
            <div>
              <dt>Plano</dt>
              <dd>{subscription?.plan_code || 'Nao configurado'}</dd>
            </div>
            <div>
              <dt>Status bruto</dt>
              <dd>{subscription?.status || 'NONE'}</dd>
            </div>
            <div>
              <dt>Inicio</dt>
              <dd>{formatDateTime(subscription?.started_at)}</dd>
            </div>
            <div>
              <dt>Expira em</dt>
              <dd>{formatDateTime(subscription?.expires_at)}</dd>
            </div>
            <div>
              <dt>Cancelada em</dt>
              <dd>{formatDateTime(subscription?.cancelled_at)}</dd>
            </div>
            <div>
              <dt>Registro</dt>
              <dd>{subscription?.has_subscription ? 'Configurado' : 'Nao configurado'}</dd>
            </div>
          </dl>
        </section>

        <section className="portal-section-card portal-section-card--finance-highlight portal-section-card--finance-deposit">
          <div className="portal-section-card__header">
            <div>
              <p className="portal-section-card__eyebrow">Impacto operacional</p>
              <h3>O que esta liberado agora</h3>
            </div>
          </div>

          <div className="portal-subscription-grid">
            <article className="portal-subscription-item">
              <strong>Criar clubs</strong>
              <p className="portal-muted">
                {subscription?.can_create_club
                  ? 'O operador pode criar novos clubs.'
                  : 'Criacao de novos clubs bloqueada ate regularizacao.'}
              </p>
            </article>
            <article className="portal-subscription-item">
              <strong>Criar mesas</strong>
              <p className="portal-muted">
                {subscription?.can_operate_club
                  ? 'Mesas novas podem ser criadas normalmente.'
                  : 'Criacao de mesas bloqueada enquanto a assinatura estiver irregular.'}
              </p>
            </article>
            <article className="portal-subscription-item">
              <strong>Iniciar jogos</strong>
              <p className="portal-muted">
                {subscription?.can_operate_club
                  ? 'O club pode iniciar novas partidas.'
                  : 'Inicio de jogo bloqueado ate regularizacao da assinatura.'}
              </p>
            </article>
          </div>

          {subscription?.regularization_message ? (
            <div className="portal-banner portal-banner--warning">
              {subscription.regularization_message}
            </div>
          ) : null}

          {subscription?.notes ? (
            <div className="portal-receipt">
              <h4>Observacoes</h4>
              <p>{subscription.notes}</p>
            </div>
          ) : null}
        </section>
      </div>

      <section className="portal-section-card portal-section-card--finance-highlight portal-section-card--finance-manual">
        <div className="portal-section-card__header">
          <div>
            <p className="portal-section-card__eyebrow">Gestao comercial</p>
            <h3>Contratar, atualizar e regularizar assinatura</h3>
          </div>
        </div>

        <form
          className="portal-form-grid"
          onSubmit={(event) => {
            event.preventDefault();
            if (hasCurrentContract) {
              void handleUpdateContract();
              return;
            }
            if (isCancelledContract) {
              void handleResumeContract();
              return;
            }
            void handleCreateContract();
          }}
        >
          <label>
            <span>Plano</span>
            <select
              value={selectedPlanCode}
              onChange={(event) => setSelectedPlanCode(event.target.value)}
              disabled={submitting || loading || !plans.length}
            >
              {plans.map((plan) => (
                <option key={plan.plan_code} value={plan.plan_code}>
                  {plan.display_name} ({plan.currency_code} {Number(plan.price_reais || 0).toFixed(2)})
                </option>
              ))}
            </select>
            <small className="portal-form-helper">Planos ativos do catalogo oficial da plataforma.</small>
          </label>

          <label>
            <span>Forma de pagamento</span>
            <select
              value={selectedBillingType}
              onChange={(event) => setSelectedBillingType(event.target.value as ClubSubscriptionBillingType)}
              disabled={submitting || loading}
            >
              {BILLING_TYPE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <small className="portal-form-helper">Define como o Asaas gera as proximas cobrancas da assinatura.</small>
          </label>

          <label className="portal-form-grid__full">
            <span>Motivo de cancelamento (opcional)</span>
            <input
              type="text"
              maxLength={200}
              value={cancelReason}
              onChange={(event) => setCancelReason(event.target.value)}
              placeholder="Ex.: encerramento do club, troca de operacao"
              disabled={submitting || loading}
            />
          </label>

          <div className="portal-form-grid__full portal-toggle-grid">
            <label className="portal-toggle-card">
              <input
                type="checkbox"
                checked={termsAccepted}
                onChange={(event) => setTermsAccepted(event.target.checked)}
                disabled={submitting || loading}
              />
              <div>
                <strong>Aceite do termo de assinatura (v{CLUB_OWNER_TERMS_VERSION})</strong>
                <span>
                  Declaro que a 3BET fornece software de gestao do club e nao realiza custodia de valores nem se responsabiliza pelas movimentacoes financeiras entre club e membros.
                </span>
              </div>
            </label>
          </div>

          <div className="portal-banner portal-banner--warning portal-form-grid__full">
            Termo completo: consulte o documento oficial com a equipe da plataforma (versao {CLUB_OWNER_TERMS_VERSION}).
          </div>

          <div className="portal-form-grid__actions portal-form-grid__full portal-card-actions">
            <span className="portal-muted">
              Contrato atual: {contract?.status || 'SEM_CONTRATO'} | Metodo: {contract?.payment_method_preference || '-'}
            </span>
            <div className="portal-card-actions portal-card-actions--end">
              {!hasCurrentContract && !isCancelledContract ? (
                <button
                  type="submit"
                  className="portal-primary-button"
                  disabled={submitting || loading || !selectedPlanCode || !termsAccepted}
                >
                  {submitting ? 'Processando...' : 'Contratar assinatura'}
                </button>
              ) : null}

              {hasCurrentContract ? (
                <>
                  <button
                    type="submit"
                    className="portal-secondary-button"
                    disabled={submitting || loading || !selectedPlanCode}
                  >
                    {submitting ? 'Processando...' : 'Atualizar plano/metodo'}
                  </button>
                  <button
                    type="button"
                    className="portal-secondary-button"
                    disabled={submitting || loading}
                    onClick={() => void handleCancelContract()}
                  >
                    {submitting ? 'Processando...' : 'Cancelar assinatura'}
                  </button>
                </>
              ) : null}

              {isCancelledContract ? (
                <button
                  type="button"
                  className="portal-primary-button"
                  disabled={submitting || loading || !termsAccepted}
                  onClick={() => void handleResumeContract()}
                >
                  {submitting ? 'Processando...' : 'Retomar assinatura'}
                </button>
              ) : null}
            </div>
          </div>
        </form>
      </section>

      <section className="portal-section-card portal-section-card--finance-highlight portal-section-card--finance-ledger">
        <div className="portal-section-card__header">
          <div>
            <p className="portal-section-card__eyebrow">Cobranca atual</p>
            <h3>Fatura da assinatura</h3>
          </div>
          <span className={`portal-status-pill ${getSubscriptionToneClass(subscription?.status || null)}`}>
            {getInvoiceStatusLabel(invoice?.status)}
          </span>
        </div>

        {invoice ? (
          <div className="portal-detail-grid">
            <div>
              <dt>Valor</dt>
              <dd>BRL {Number(invoice.amount_reais || 0).toFixed(2)}</dd>
            </div>
            <div>
              <dt>Vencimento</dt>
              <dd>{formatDateTime(invoice.due_date)}</dd>
            </div>
            <div>
              <dt>Pagamento</dt>
              <dd>{formatDateTime(invoice.paid_at)}</dd>
            </div>
            <div>
              <dt>Atualizada em</dt>
              <dd>{formatDateTime(invoice.updated_at)}</dd>
            </div>
            <div>
              <dt>ID Asaas</dt>
              <dd>{invoice.provider_payment_id}</dd>
            </div>
            <div>
              <dt>Tipo</dt>
              <dd>{invoice.billing_type}</dd>
            </div>
          </div>
        ) : (
          <p className="portal-muted">Nenhuma cobranca atual encontrada para o contrato ativo.</p>
        )}

        <div className="portal-card-actions portal-card-actions--end" style={{ marginTop: 16 }}>
          <button type="button" className="portal-secondary-button" onClick={() => void handleRefreshInvoice()} disabled={submitting || loading}>
            {submitting ? 'Sincronizando...' : 'Sincronizar cobranca'}
          </button>
          {invoice?.invoice_url ? (
            <button
              type="button"
              className="portal-primary-button"
              onClick={() => window.open(invoice.invoice_url as string, '_blank', 'noopener,noreferrer')}
            >
              Abrir fatura
            </button>
          ) : null}
          {invoice?.bank_slip_url ? (
            <button
              type="button"
              className="portal-secondary-button"
              onClick={() => window.open(invoice.bank_slip_url as string, '_blank', 'noopener,noreferrer')}
            >
              Abrir boleto
            </button>
          ) : null}
        </div>

        {invoice?.pix_copy_paste_code ? (
          <div className="portal-banner portal-banner--warning" style={{ marginTop: 16 }}>
            Pix copia e cola: {invoice.pix_copy_paste_code}
          </div>
        ) : null}
      </section>

      <section className="portal-section-card portal-section-card--finance-highlight portal-section-card--finance-ledger">
        <div className="portal-section-card__header">
          <div>
            <p className="portal-section-card__eyebrow">Historico financeiro</p>
            <h3>Cobrancas da assinatura</h3>
          </div>
          <span className="portal-chip">{invoiceHistory.length} registro(s)</span>
        </div>

        <div className="portal-table-wrap">
          <table className="portal-table">
            <thead>
              <tr>
                <th>Vencimento</th>
                <th>Status</th>
                <th>Valor</th>
                <th>Forma</th>
                <th>Pago em</th>
                <th>Acao</th>
              </tr>
            </thead>
            <tbody>
              {invoiceHistory.length ? (
                invoiceHistory.map((entry) => (
                  <tr key={entry.id}>
                    <td>{formatDateTime(entry.due_date)}</td>
                    <td>{getInvoiceStatusLabel(entry.status)}</td>
                    <td>BRL {Number(entry.amount_reais || 0).toFixed(2)}</td>
                    <td>{entry.billing_type || '-'}</td>
                    <td>{formatDateTime(entry.paid_at)}</td>
                    <td>
                      {entry.invoice_url ? (
                        <button
                          type="button"
                          className="portal-secondary-button portal-secondary-button--compact"
                          onClick={() => window.open(entry.invoice_url as string, '_blank', 'noopener,noreferrer')}
                        >
                          Abrir
                        </button>
                      ) : (
                        <span className="portal-muted">-</span>
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6}>Nenhuma cobranca registrada ainda.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section className="portal-section-card portal-section-card--finance-highlight portal-section-card--finance-ledger">
        <div className="portal-section-card__header">
          <div>
            <p className="portal-section-card__eyebrow">Historico</p>
            <h3>Renovacoes e alteracoes da assinatura</h3>
            <p className="portal-muted">
              Trilha cronologica do contrato com status, vigencia, pagamentos e origem de cada alteracao.
            </p>
          </div>
          <span className="portal-chip">{history.length} registro(s)</span>
        </div>

        {history.length ? (
          <div className="portal-table-wrap">
            <table className="portal-table">
              <thead>
                <tr>
                  <th>Quando</th>
                  <th>Evento</th>
                  <th>Status</th>
                  <th>Plano / vigencia</th>
                  <th>Pagamento</th>
                  <th>Origem</th>
                  <th>Observacao</th>
                </tr>
              </thead>
              <tbody>
                {history.map((entry) => (
                  <tr key={entry.id}>
                    <td>{formatDateTime(entry.recorded_at)}</td>
                    <td>{getHistoryChangeLabel(entry.change_type)}</td>
                    <td>{getSubscriptionStatusLabel(entry.status)}</td>
                    <td>
                      <div className="portal-table__cell-stack">
                        <strong>{entry.plan_code}</strong>
                        <span>
                          Inicio: {formatDateTime(entry.started_at)} | Expira: {formatDateTime(entry.expires_at)}
                        </span>
                      </div>
                    </td>
                    <td>{formatDateTime(entry.last_payment_at)}</td>
                    <td>{entry.created_by_name || 'Sistema'}</td>
                    <td>{entry.notes || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="portal-muted">Nenhum historico de renovacao disponivel para esta assinatura.</p>
        )}
      </section>

      <section className="portal-section-card portal-section-card--finance-highlight portal-section-card--finance-manual">
        <div className="portal-section-card__header">
          <div>
            <p className="portal-section-card__eyebrow">Catalogo</p>
            <h3>Planos disponiveis</h3>
          </div>
          <span className="portal-chip">{plans.length} plano(s)</span>
        </div>
        <p className="portal-muted">
          Catalogo carregado do backend para preparar a contratacao automatica da assinatura.
        </p>
        <div className="portal-table-wrap">
          <table className="portal-table">
            <thead>
              <tr>
                <th>Plano</th>
                <th>Ciclo</th>
                <th>Preco</th>
                <th>Trial</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {plans.length ? (
                plans.map((plan) => (
                  <tr key={plan.plan_code}>
                    <td>
                      <div className="portal-table__cell-stack">
                        <strong>{plan.display_name}</strong>
                        <span>{plan.plan_code}</span>
                      </div>
                    </td>
                    <td>{plan.billing_cycle}</td>
                    <td>
                      {plan.currency_code} {Number(plan.price_reais || 0).toFixed(2)}
                    </td>
                    <td>{plan.trial_days === null ? '-' : `${plan.trial_days} dia(s)`}</td>
                    <td>{plan.is_default ? 'Padrao' : 'Ativo'}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={5}>Nenhum plano ativo encontrado.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
};

export default SubscriptionPage;
